import { ChevronRight, MapPin, PawPrint, Pill } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { PublicOrder } from '@snootsvet/shared/portal'
import { formatQuantity, pluralizeUnit } from '@snootsvet/shared/portal'

import { routes } from '@/customer/routes'

const MAX_ITEMS = 4

/** Returns the earliest canonical ISO timestamp in an order collection. */
function earliestCreatedAt(orders: PublicOrder[]) {
  return orders.reduce(
    (earliest, order) => (order.createdAt < earliest ? order.createdAt : earliest),
    orders[0].createdAt
  )
}

/** Picks the clinic with the most ready orders, favouring the earliest order when tied. */
function selectMainClinicOrders(orders: PublicOrder[]) {
  const ordersByClinic = new Map<string, PublicOrder[]>()
  for (const order of orders) {
    const clinicOrders = ordersByClinic.get(order.clinic.id) ?? []
    clinicOrders.push(order)
    ordersByClinic.set(order.clinic.id, clinicOrders)
  }

  return [...ordersByClinic.values()].sort((a, b) => {
    const countDifference = b.length - a.length
    if (countDifference) return countDifference
    return earliestCreatedAt(a).localeCompare(earliestCreatedAt(b))
  })[0]
}

/** Ready-for-collection orders on the portal home page. */
export function ReadyOrdersCard({
  orders,
  className,
}: {
  orders: PublicOrder[]
  className?: string
}) {
  const mainClinicOrders = selectMainClinicOrders(orders)
  if (!mainClinicOrders) return null

  const visible = mainClinicOrders.slice(0, MAX_ITEMS)
  const remaining = orders.length - visible.length
  const otherClinics = new Set(orders.map((order) => order.clinic.id)).size - 1
  const clinic = mainClinicOrders[0].clinic

  return (
    <Link
      to={routes.account.orders}
      className={`group flex w-full flex-col overflow-hidden rounded-2xl border-2 border-foreground shadow-[6px_6px_0_0] shadow-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className ?? ''}`}
    >
      <div className="flex items-center justify-between bg-green px-4 py-2.5 text-foreground">
        <p className="text-button-sm">
          Ready for collection{orders.length > 1 ? ` · ${orders.length} items` : ''}
        </p>
        <ChevronRight
          className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </div>
      <div className="flex flex-col gap-3 bg-muted p-4">
        {visible.map((order) => (
          <div
            key={order.id}
            className="flex flex-col gap-y-1 gap-x-3 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <p className="text-button">{order.productName}</p>
            <div className="flex items-center gap-3 text-label-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <PawPrint className="size-3.5" aria-hidden />
                {order.petName}
              </span>
              <span className="flex items-center gap-1">
                <Pill className="size-3.5" aria-hidden />
                {formatQuantity(order.quantity)} {pluralizeUnit(order.unit, order.quantity)}
              </span>
            </div>
          </div>
        ))}
        {remaining > 0 && (
          <p className="text-fine-print text-muted-foreground">+{remaining} other orders</p>
        )}
        <p className="flex items-center gap-1 text-label-sm text-muted-foreground">
          <MapPin className="size-3.5" aria-hidden />
          Collect from {clinic.name}
          {otherClinics > 0 ? ` · +${otherClinics} other clinic${otherClinics > 1 ? 's' : ''}` : ''}
        </p>
      </div>
    </Link>
  )
}
